import type { FilterName } from "../PromptFiltersV2/types";
import { type SavedPromptsFilters } from "../SavedPromptsFilter/SavedPromptsFilter";
import type { Prompt } from "../data";

export type PromptLibraryState = {
  allPrompts: Prompt[];
  displayedPrompts: Prompt[];
  selectedFacets: Record<FilterName, string[]>;
  showFavoritesOnly: boolean;
  showSavedPromptsOnly: boolean;
  savedPromptsFilter: SavedPromptsFilters;
  hasBookmarkedPrompts?: boolean;
};

export type PromptLibraryAction =
  | {
      type: "SET_SELECTED_FACETS";
      payload: { facets: Record<FilterName, string[]> };
    }
  | {
      type: "SET_PROMPTS";
      payload: { prompts: Prompt[] };
    }
  | {
      type: "TOGGLE_FAVORITE_ONLY";
      payload: { favorited: boolean };
    }
  | {
      type: "TOGGLE_SAVED_PROMPTS_ONLY";
      payload: { saved: boolean };
    }
  | {
      type: "SET_SAVED_PROMPTS_FILTER";
      payload: { filter: SavedPromptsFilters };
    }
  | {
      type: "DELETE_UNDO";
      payload: { prompt: Prompt; promptPosition: number };
    }
  | {
      type: "DELETE_SUCCEED";
      payload: { prompt: Prompt };
    }
  | {
      type: "SHARE_TO_WORKGROUP_PROMPT_SELECTED";
      payload: { prompts: Prompt[] };
    }
  | {
      type: "REMOVE_FROM_WORKGROUP_PROMPT_SELECTED";
      payload: { prompts: Prompt[] };
    };

export const DisplayCategoryKey = "displayCategory";
export const ProductNameKey = "products/name";
export const JobTypeKey = "jobType";

export const filterPrompts = (
  prompts: Prompt[],
  showFavorites: boolean,
  showSaved: boolean,
  savedPromptsFilter: SavedPromptsFilters,
  filters: Record<FilterName, string[]>
) => {
  let filterPrompts = showFavorites
    ? prompts.filter((p) => p.UserActivity?.Favorite?.IsFavorite)
    : prompts;

  filterPrompts = showSaved
    ? filterPrompts.filter(
        (p) => p.Origin === "User" || p.UserActivity?.Favorite?.IsFavorite
      )
    : filterPrompts.filter((p) => p.Origin !== "User");

  if (showSaved && savedPromptsFilter === "CreatedByMe") {
    filterPrompts = filterPrompts.filter((p) => p.Origin === "User");
  } else if (showSaved && savedPromptsFilter === "CreatedByMicrosoft") {
    filterPrompts = filterPrompts.filter((p) => p.Origin !== "User");
  }

  // Loop through each key-value pair in the filters object
  for (const [key, values] of Object.entries(filters)) {
    switch (key) {
      case "DisplayCategory": // Bizchat is using 'DisplayCategory' instead of 'displayCategory' in their fallback data. Need to work with them to remove it. so everybody is using the same thing.
      case "displayCategory":
        filterPrompts =
          values.length > 0
            ? filterPrompts.filter((f) => values.includes(f.DisplayCategory))
            : filterPrompts;
        break;
      case "products/name":
        filterPrompts =
          values.length > 0
            ? filterPrompts.filter((f) =>
                values.some((item) => f.Products.includes(item))
              )
            : filterPrompts;
        break;
      case "jobType":
        filterPrompts =
          values.length > 0
            ? filterPrompts.filter((f) =>
                values.some((item) =>
                  f.Metadata?.find((m) => {
                    return m.Value === item;
                  })
                )
              )
            : filterPrompts;
        break;
    }
  }

  return filterPrompts;
};

export const reducer = (
  state: PromptLibraryState,
  action: PromptLibraryAction
): PromptLibraryState => {
  switch (action.type) {
    case "SET_PROMPTS": {
      // we always want to display the user-authored prompts before Microsoft created ones
      const allPrompts = action.payload.prompts.sort((prompt) =>
        prompt.Origin === "User" ? -1 : 1
      );

      return {
        ...state,
        allPrompts,
        displayedPrompts: filterPrompts(
          allPrompts,
          state.showFavoritesOnly,
          state.showSavedPromptsOnly,
          state.savedPromptsFilter,
          state.selectedFacets
        ),
        hasBookmarkedPrompts: allPrompts.some(
          (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
        ),
      };
    }
    case "SET_SELECTED_FACETS":
      return {
        ...state,
        selectedFacets: action.payload.facets,
        displayedPrompts: filterPrompts(
          state.allPrompts,
          state.showFavoritesOnly,
          state.showSavedPromptsOnly,
          state.savedPromptsFilter,
          action.payload.facets
        ),
      };
    case "TOGGLE_FAVORITE_ONLY":
      return {
        ...state,
        showFavoritesOnly: action.payload.favorited,
        displayedPrompts: filterPrompts(
          state.allPrompts,
          action.payload.favorited,
          state.showSavedPromptsOnly,
          state.savedPromptsFilter,
          state.selectedFacets
        ),
      };
    case "TOGGLE_SAVED_PROMPTS_ONLY":
      return {
        ...state,
        showSavedPromptsOnly: action.payload.saved,
        displayedPrompts: filterPrompts(
          state.allPrompts,
          state.showFavoritesOnly,
          action.payload.saved,
          state.savedPromptsFilter,
          state.selectedFacets
        ),
      };
    case "SET_SAVED_PROMPTS_FILTER":
      return {
        ...state,
        savedPromptsFilter: action.payload.filter,
        displayedPrompts: filterPrompts(
          state.allPrompts,
          state.showFavoritesOnly,
          state.showSavedPromptsOnly,
          action.payload.filter,
          state.selectedFacets
        ),
      };
    case "DELETE_UNDO": {
      const allPrompts = [...state.allPrompts];

      // make sure the prompt is not already in the list
      if (
        allPrompts.findIndex(
          (prompt) => prompt.Id === action.payload.prompt.Id
        ) === -1
      ) {
        allPrompts.splice(
          action.payload.promptPosition,
          0,
          action.payload.prompt
        );
      }

      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.showFavoritesOnly,
        state.showSavedPromptsOnly,
        state.savedPromptsFilter,
        state.selectedFacets
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length + 1
      );

      return {
        ...state,
        allPrompts,
        displayedPrompts,
      };
    }
    case "DELETE_SUCCEED": {
      const allPrompts = [...state.allPrompts].filter(
        (prompt) => prompt.Id !== action.payload.prompt.Id
      );
      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.showFavoritesOnly,
        state.showSavedPromptsOnly,
        state.savedPromptsFilter,
        state.selectedFacets
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length
      );

      return {
        ...state,
        allPrompts,
        displayedPrompts,
      };
    }
    default:
      return state;
  }
};
